import { CategoryName, DbId, IInParamsValues } from '@infotech-soe/metaschema';
import { createSelector } from '@reduxjs/toolkit';

import { CommandName } from '../../services/commands/CommandViewModel';
import UniqueId from '../../services/uid/UniqueId';
import { AppState } from '../reducers/root';
import {
  BalanceIndexes,
  BalanceOptions,
  BalanceRecords,
  BalanceStateEntity,
  BalanceValues,
} from '../slices/balance';
import { ContentEntityType } from '../slices/content';

import { getCategoryState } from './category';

export const getBalanceStateEntity = (
  state: AppState,
  id: UniqueId
): BalanceStateEntity => state.balance[id];

export const getBalanceStreamId = (state: AppState, id: UniqueId) =>
  state.balance[id].streamId;

export const getBalanceTitle = (state: AppState, id: UniqueId): string =>
  state.balance[id].title;

export const getBalanceIndexes = (
  state: AppState,
  id: UniqueId
): BalanceIndexes => state.balance[id].indexes;

export const getBalanceRecords = (
  state: AppState,
  id: UniqueId
): BalanceRecords => state.balance[id].records;

export const getBalanceValues = (
  state: AppState,
  id: UniqueId
): BalanceValues => state.balance[id].values;

export const getBalanceIsChanged = (state: AppState, id: UniqueId): boolean =>
  state.balance[id].isChanged;

export const getBalanceShowConfirm = (state: AppState, id: UniqueId): boolean =>
  state.balance[id].showConfirm;

export const getBalanceCommand = (
  state: AppState,
  id: UniqueId
): CommandName | null => state.balance[id].command;

export const getBalanceByDatasetId = createSelector(
  [
    (state: AppState) => state.balance,
    (_state: AppState, datasetId: UniqueId) => datasetId,
  ],
  (balance, datasetId) =>
    Object.entries(balance).find(
      ([, { datasetId: dataset }]) => dataset === datasetId
    )?.[0]
);

export const getBalanceBySubReportId = createSelector(
  [
    (state: AppState) => state.balance,
    (_state: AppState, subReportId: DbId) => subReportId,
  ],
  (balance, subReportId) =>
    Object.entries(balance).find(
      ([, { subReportId: subReport }]) => subReport === subReportId
    )?.[0]
);

export const getBalanceOptions = (
  state: AppState,
  id: UniqueId
): BalanceOptions => state.balance[id].options;

export const getBalanceIsRefresh = (state: AppState, id: UniqueId): boolean =>
  state.balance[id].isRefresh;

export const getBalanceIsCalculate = (state: AppState, id: UniqueId): boolean =>
  state.balance[id].isCalculate;

export const getBalanceIsPending = (state: AppState, id: UniqueId): boolean =>
  state.balance[id].pendingCount > 0;

export const getBalanceContentTab = (
  state: AppState,
  category: CategoryName,
  inParams: IInParamsValues
): UniqueId | undefined =>
  Object.entries(state.content.entities).find(([, entity]) => {
    if (entity.type !== ContentEntityType.CATEGORY) return false;
    const source = getCategoryState(state, entity.content)?.source;
    if (source?.category !== category) return false;
    return Object.entries(inParams).every(
      ([key, value]) => source.inParams?.[key] === value
    );
  })?.[0];
